import React, {
    useEffect,
    useRef,
    useState
} from "react";

import {
    StandardModalText,
    portalColors
} from "src/components";
import SearchIcon from "src/img/search-icon.svg";

import CheckInSearchResult from "./CheckInSearchResult";
import { CHECK_IN_CATEGORY } from "./checkIn";
import { searchGoogleCheckInPlaces } from "./googlePlaces";
import {
    LocationActionIcon,
    LocationAddress,
    LocationButton,
    LocationFieldWrap,
    LocationInput,
    LocationName,
    ResultsDropdown,
    ResultsList,
    ScreenReaderText,
    SelectedLocationSummary
} from "./CheckInControls.styled";

const emptySelectedPlace = {
    placeName: undefined,
    placeId: undefined,
    formattedAddress: undefined,
    latitude: undefined,
    longitude: undefined,
    provider: undefined,
    providerPlaceTypes: undefined,
    placeImageReference: undefined
};

const CheckInLocationSearch = ({
    value,
    selectedCategory,
    onSelect,
    onClearSelectedPlace = () => {},
    openOnCategoryChange = false,
    placeholder = "Where are you?"
}) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState(false);
    const [coordinates, setCoordinates] = useState(null);
    const [locationResolved, setLocationResolved] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const inputRef = useRef(null);
    const previousCategoryRef = useRef(selectedCategory);
    const searchRequestRef = useRef(0);
    const effectiveCategory = selectedCategory || CHECK_IN_CATEGORY.LOCATION;

    useEffect(() => {
        setQuery(value?.placeName || "");
    }, [value?.placeName]);

    useEffect(() => {
        const previousCategory = previousCategoryRef.current;

        previousCategoryRef.current = selectedCategory;

        if (!openOnCategoryChange || previousCategory === selectedCategory) {
            return;
        }

        setQuery("");
        setResults([]);
        setShowResults(Boolean(selectedCategory));
    }, [openOnCategoryChange, selectedCategory]);

    useEffect(() => {
        if (!showResults) {
            return undefined;
        }

        inputRef.current?.focus();

        setCoordinates(null);
        setLocationResolved(false);

        if (typeof navigator === "undefined" || !navigator.geolocation) {
            setLocationResolved(true);
            return undefined;
        }

        let mounted = true;

        navigator.geolocation.getCurrentPosition(
            position => {
                if (mounted) {
                    setCoordinates({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    });
                    setLocationResolved(true);
                }
            },
            () => {
                if (mounted) {
                    setCoordinates(null);
                    setLocationResolved(true);
                }
            },
            {
                enableHighAccuracy: false,
                maximumAge: 300000,
                timeout: 8000
            }
        );

        return () => {
            mounted = false;
        };
    }, [effectiveCategory, showResults]);

    useEffect(() => {
        if (!showResults) {
            return undefined;
        }

        const trimmedQuery = query.trim();

        if (!trimmedQuery && effectiveCategory && !locationResolved) {
            setLoading(true);
            setSearchError(false);
            setResults([]);
            return undefined;
        }

        const requestId = searchRequestRef.current + 1;

        searchRequestRef.current = requestId;

        let cancelled = false;
        const timeout = setTimeout(async () => {
            setLoading(true);
            setSearchError(false);

            try {
                const locations = await searchGoogleCheckInPlaces({
                    query: trimmedQuery,
                    coordinates: trimmedQuery ? null : coordinates,
                    category: effectiveCategory
                });

                if (cancelled || searchRequestRef.current !== requestId) {
                    return;
                }

                setResults(locations);
            } catch {
                if (cancelled || searchRequestRef.current !== requestId) {
                    return;
                }

                setSearchError(true);
                setResults([]);
            } finally {
                if (!cancelled && searchRequestRef.current === requestId) {
                    setLoading(false);
                }
            }
        }, trimmedQuery ? 250 : 0);

        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
    }, [
        coordinates,
        effectiveCategory,
        locationResolved,
        query,
        showResults
    ]);

    const openSearch = () => {
        setShowResults(true);

        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    };

    const handleSelectedLocation = location => {
        onSelect(location);
        setQuery(location.placeName || "");
        setShowResults(false);
    };

    return (
        <LocationFieldWrap
            onBlur={event => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    setShowResults(false);
                }
            }}
        >
            <LocationButton
                onClick={openSearch}
            >
                {value?.placeName && !showResults ? (
                    <SelectedLocationSummary>
                        <LocationName>{value.placeName}</LocationName>
                        {value.formattedAddress && (
                            <LocationAddress>
                                {value.formattedAddress}
                            </LocationAddress>
                        )}
                    </SelectedLocationSummary>
                ) : (
                    <LocationInput
                        ref={inputRef}
                        value={query}
                        placeholder={placeholder}
                        onFocus={() => setShowResults(true)}
                        onClick={() => setShowResults(true)}
                        onChange={event => {
                            const nextQuery = event.target.value;

                            setQuery(nextQuery);

                            if (value?.placeName && nextQuery !== value.placeName) {
                                onClearSelectedPlace(emptySelectedPlace);
                            }

                            setShowResults(true);
                        }}
                    />
                )}

                <LocationActionIcon aria-hidden="true">
                    <img src={SearchIcon} alt="" />
                </LocationActionIcon>
                <ScreenReaderText>Search location</ScreenReaderText>
            </LocationButton>

            {showResults && (
                <ResultsDropdown>
                    {loading && (
                        <StandardModalText color={portalColors.mutedText}>
                            Searching locations...
                        </StandardModalText>
                    )}
                    {searchError && (
                        <StandardModalText color={portalColors.danger}>
                            Locations could not be loaded.
                        </StandardModalText>
                    )}
                    {!loading && !searchError && results.length === 0 && (
                        <StandardModalText color={portalColors.mutedText}>
                            {query.trim()
                                ? "No locations found. Try a different search."
                                : "No locations found."}
                        </StandardModalText>
                    )}
                    <ResultsList>
                        {results.map(location => (
                            <CheckInSearchResult
                                key={location.placeId}
                                location={location}
                                selectedCategory={effectiveCategory}
                                onSelect={handleSelectedLocation}
                            />
                        ))}
                    </ResultsList>
                </ResultsDropdown>
            )}
        </LocationFieldWrap>
    );
};

export default CheckInLocationSearch;
